import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

/**
 * Watches for two distinct conditions and bounces the user to Zitadel logout
 * whenever the earlier of the two fires:
 *
 *   1. IDLE        — `timeoutMs` of no user input (mouse/key/scroll/touch)
 *   2. ABSOLUTE    — `maxSessionMs` since the user actually authenticated.
 *                    Read from the OIDC id_token's standard `auth_time` claim,
 *                    so silent renew (which keeps refreshing access tokens) does
 *                    NOT push this deadline back.
 *
 * `warningMs` is the countdown window. When a trigger fires, the modal shows
 * with a "you'll be redirected to login" message. For idle, the user can click
 * "Stay signed in" to reset and continue. For absolute expiry, there's no
 * "stay" — the only choice is to re-login now or wait for the redirect.
 *
 * Why we need this even with Zitadel's server-side enforcement:
 *   - Idle Login Lifetime is reset by silent renew (the lib pings /token every
 *     ~55min). So a truly idle tab never expires server-side.
 *   - Absolute Login Lifetime IS enforced server-side, but without this
 *     component the user finds themselves bounced to /login mid-action with
 *     no warning. This pre-empts the bounce with a polite countdown.
 */
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

const SessionGuard = ({ timeoutMs, maxSessionMs, warningMs }) => {
  // Required props — no defaults. Callers must pass values (driven by env).
  if (
    typeof timeoutMs !== 'number' ||
    typeof maxSessionMs !== 'number' ||
    typeof warningMs !== 'number'
  ) {
    throw new Error(
      'SessionGuard: timeoutMs, maxSessionMs, and warningMs are required numeric props.'
    );
  }

  const auth = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // 'idle' or 'absolute' — drives the modal copy + button choices
  const [reason, setReason] = useState(null);

  // Refs keep handlers stable across renders and avoid stale-closure bugs
  // when the effect re-binds.
  const idleWarnTimer = useRef(null);
  const absoluteWarnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const tickInterval = useRef(null);
  const warningOpenRef = useRef(false);
  const authRef = useRef(auth);
  authRef.current = auth;
  const handlersRef = useRef({ stay: () => {}, logoutNow: () => {} });

  useEffect(() => {
    if (!auth.isAuthenticated) return undefined;

    // Absolute deadline = when the user originally authenticated + max window.
    // `auth_time` is a standard OIDC claim, expressed in epoch seconds.
    const authTime = auth.user?.profile?.auth_time;
    const sessionExpiresAt = authTime
      ? authTime * 1000 + maxSessionMs
      : Date.now() + maxSessionMs; // fallback if claim is missing

    const clearAll = () => {
      if (idleWarnTimer.current) clearTimeout(idleWarnTimer.current);
      if (absoluteWarnTimer.current) clearTimeout(absoluteWarnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (tickInterval.current) clearInterval(tickInterval.current);
      idleWarnTimer.current = null;
      absoluteWarnTimer.current = null;
      logoutTimer.current = null;
      tickInterval.current = null;
    };

    const fireLogout = () => {
      clearAll();
      authRef.current.signoutRedirect();
    };

    const showWarning = (cause) => {
      // Don't open a second warning if one's already up.
      if (warningOpenRef.current) return;
      warningOpenRef.current = true;
      setReason(cause);
      setWarningOpen(true);
      setSecondsLeft(Math.ceil(warningMs / 1000));
      tickInterval.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
      logoutTimer.current = setTimeout(fireLogout, warningMs);
    };

    const armIdleTimer = () => {
      if (idleWarnTimer.current) clearTimeout(idleWarnTimer.current);
      idleWarnTimer.current = setTimeout(
        () => showWarning('idle'),
        Math.max(0, timeoutMs - warningMs)
      );
    };

    const armAbsoluteTimer = () => {
      if (absoluteWarnTimer.current) clearTimeout(absoluteWarnTimer.current);
      const msUntilWarn = sessionExpiresAt - warningMs - Date.now();
      if (msUntilWarn <= 0) {
        // Session already past the warning point — fire immediately.
        showWarning('absolute');
      } else {
        absoluteWarnTimer.current = setTimeout(
          () => showWarning('absolute'),
          msUntilWarn
        );
      }
    };

    const resetAll = () => {
      clearAll();
      warningOpenRef.current = false;
      setWarningOpen(false);
      setReason(null);
      armIdleTimer();
      armAbsoluteTimer();
    };

    const onActivity = () => {
      // While a warning is up, background interaction shouldn't dismiss it —
      // explicit button only. Also: re-arming the idle timer is the ONLY
      // thing activity does; the absolute timer is fixed.
      if (warningOpenRef.current) return;
      armIdleTimer();
    };

    IDLE_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    resetAll();

    handlersRef.current = {
      stay: () => {
        // "Stay signed in" only makes sense for idle. For an absolute expiry
        // the user must re-authenticate — there's no extending it client-side.
        if (warningOpenRef.current) {
          // Read reason from state via a closure — for the dialog's onClose,
          // we need the latest value. Easier to track in a ref too.
          // Defensive: if reason is 'absolute', "stay" becomes logout-and-relogin.
          // (We rely on the buttons only showing the right action — see render.)
          resetAll();
        }
      },
      logoutNow: fireLogout,
    };

    return () => {
      clearAll();
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [auth.isAuthenticated, auth.user, timeoutMs, maxSessionMs, warningMs]);

  const isIdle = reason === 'idle';
  const isAbsolute = reason === 'absolute';

  return (
    <Dialog
      open={warningOpen}
      onClose={() => {
        // Backdrop click: for idle, treat as "stay". For absolute, do nothing
        // (force the explicit button choice).
        if (isIdle) handlersRef.current.stay();
      }}
      sx={{ zIndex: 100000 }}
    >
      <DialogTitle>
        {isAbsolute ? 'Session ending' : 'Are you still there?'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isAbsolute ? (
            <>
              Your session expires in <strong>{secondsLeft}</strong>{' '}
              second{secondsLeft === 1 ? '' : 's'}. You&apos;ll be redirected
              to the login page — please sign in again to continue.
            </>
          ) : (
            <>
              We haven&apos;t seen any activity for a while. You&apos;ll be
              signed out in <strong>{secondsLeft}</strong>{' '}
              second{secondsLeft === 1 ? '' : 's'}.
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {isAbsolute ? (
          <Button
            onClick={() => handlersRef.current.logoutNow()}
            variant="contained"
            autoFocus
          >
            Log in again
          </Button>
        ) : (
          <>
            <Button
              onClick={() => handlersRef.current.logoutNow()}
              color="inherit"
            >
              Log out now
            </Button>
            <Button
              onClick={() => handlersRef.current.stay()}
              variant="contained"
              autoFocus
            >
              Stay signed in
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionGuard;
